import { useEffect, useRef } from 'react';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export const useDialog = (isOpen, onClose) => {
  const panelRef = useRef(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return undefined;

    const panel = panelRef.current;
    const restoreFocusTo = document.activeElement;
    const restoreOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const focusables = () => Array.from(panel?.querySelectorAll(FOCUSABLE) ?? []);
    (focusables()[0] ?? panel)?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeRef.current();
        return;
      }
      if (event.key !== 'Tab') return;

      const items = focusables();
      if (items.length === 0) {
        event.preventDefault();
        return;
      }

      const first = items[0];
      const last = items[items.length - 1];
      const target = event.shiftKey
        ? document.activeElement === first && last
        : document.activeElement === last && first;

      if (!target) return;
      event.preventDefault();
      target.focus();
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = restoreOverflow;
      restoreFocusTo?.focus?.();
    };
  }, [isOpen]);

  return panelRef;
};
